/* eslint-disable no-mixed-spaces-and-tabs */
import CreateIcon from "@mui/icons-material/Create";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import IconGroup from "../../../../../../design/icon-group";
import Highlight from "../../../../../Highlight";
import { openUserDeactivateDialog } from "../../../../store/gat-admin-slice";
import { HR_MANAGER } from "../../constant";
import { getUserRoleLabel } from "../../role-labels";
import MyChip from "../my-chip";

// Display role: the first match in priority order (HR Manager wins), which
// mirrors the previous nested ternaries.
const DISPLAY_ROLE_PRIORITY = [
  HR_MANAGER,
  "TrainingManager",
  "TrainingCoordinator",
  "DepartmentManager",
  "ShiftSupervisor",
  "ReadOnly",
  "LimitedAccessSupervisor",
];

const getDisplayRoleName = (roles: { id: number; name: string }[] = []) =>
  DISPLAY_ROLE_PRIORITY.find((name) => roles.some((role) => role.name === name));

const useSupervisorColumns = (searchText: string) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [expandWorkcenters, setExpandWorkcenters] = useState<number[]>([]);

  const onClickViewMoreWorkCenters = (id: number) => {
    const selectedWorkcentersList = expandWorkcenters.includes(id)
      ? expandWorkcenters.filter((item) => item !== id)
      : [...expandWorkcenters, id];

    setExpandWorkcenters(selectedWorkcentersList);
  };

  const userRoles = useSelector(
    (state: any) => state.user?.userData?.userRoles
  );
  const columns: GridColDef[] = [
    {
      field: "name",
      flex: 1,
      headerName: t("users_full-name"),
      minWidth: 200,
      valueGetter: (params) => {
        return `${params?.row?.firstName} ${params?.row?.lastName}`;
      },
      renderCell: (params) => {
        const fullName = `${params.row.firstName} ${params.row.lastName}`;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              minHeight: "65px",
            }}
          >
            <Tooltip title={fullName} disableInteractive>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  color: "rgba(0, 0, 0, 0.87)",
                }}
              >
                <Highlight value={fullName} searchText={searchText} />
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: t("users_email-address"),
      minWidth: 250,
      flex: 1,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.email} disableInteractive>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.87)",
                display: "flex",
                flexWrap: "wrap",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Highlight value={params.row.email} searchText={searchText} />
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "userRole",
      headerName: t("users_user-role"),
      minWidth: 120,
      flex: 1,
      renderCell: (params) => {
        // As per requirment - present its limit to show Readonly/Supervisor roles. The rest of the roles can come when we extend to Company Admin / Location Admin kind of roles.
        const roleName = getDisplayRoleName(params.row.roles);
        const role = roleName ? getUserRoleLabel(roleName, t) : "-";
        return (
          <Tooltip title={role} disableInteractive>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              <Highlight value={role} searchText={searchText} />
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: (params) => {
        const roleName = getDisplayRoleName(params.row.roles);
        return roleName ? getUserRoleLabel(roleName, t) : "";
      },
    },
    {
      field: "workcenters",
      headerName: t("users_workcenters"),
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      flex: 1,
      valueGetter: (params) => {
        return params.row.userWorkCenters?.length ?? 0;
      },
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.userWorkCenters.length} disableInteractive>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              <Highlight
                value={params.row.userWorkCenters.length}
                searchText={searchText}
              />
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "userWorkCenters",
      headerName: t("users_workcenter-names"),
      minWidth: 360,
      flex: 2,
      renderCell: (params) => {
        return (
          <Box>
            <MyChip
              searchText={searchText}
              userWorkCenters={params.row.userWorkCenters}
              rowId={params.row.id}
              expandWorkcenters={expandWorkcenters}
              onClickViewMoreWorkCenters={onClickViewMoreWorkCenters}
            />
          </Box>
        );
      },
      valueGetter: (params) => params.row.userWorkCenters?.length ?? 0,
    },
    {
      field: "managerName",
      headerName: t("users_manager"),
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => {
        return params.row?.managerName ?? "";
      },
      renderCell: (params) => {
        return (
          <Tooltip title={params.row?.managerName ?? ""} disableInteractive>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              <Highlight
                value={params.row?.managerName ?? "-"}
                searchText={searchText}
              />
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "status",
      headerName: t("users_status"),
      minWidth: 120,
      flex: 1,
      renderCell: (params) => {
        return (
          <Chip
            size="small"
            label={
              <Highlight
                value={params.row.isActive ? t("users_active") : t("users_inactive")}
                searchText={searchText}
              />
            }
            variant="filled"
            style={{
              marginTop: "4px",
              marginBottom: "4px",
              marginRight: "4px",
            }}
            sx={{
              paddingRight: "8px",
              paddingLeft: "8px",
              background: params.row.isActive ? "#E8F5E9" : "#FEEBEE",
            }}
          />
        );
      },
      valueGetter: (params) => params.row.isActive,
    },
    {
      field: "actions",
      headerName: t("common_actions"),
      headerAlign: "center",
      align: "center",
      width: 86,
      maxWidth: 86,
      cellClassName: "actions-col-cell",
      headerClassName: "actions-col-header",
      sortable: false,
      renderHeader: () => (
        <span
          style={{
            fontWeight: "bold",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {t("common_actions")}
        </span>
      ),
      renderCell: (params) => {
        const role = params.row.roles.find(
          (x: { id: number; name: string }) => x.name === HR_MANAGER
        );
        if (role?.name !== HR_MANAGER || userRoles.includes("GATAdmin")) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <IconGroup
                        icons={[
                            {
                                icon: <CreateIcon sx={{ fontSize: "16px" }} />,
                                tooltip: t("common_edit"),
                                onClick: (e: React.MouseEvent) => {
                                    params.row.changeCompanyUserData(params.row);
                                    e.stopPropagation();
                                },
                            },
                            {
                                icon: params.row.isActive ? (
                                    <PersonOffIcon sx={{ fontSize: "16px" }} />
                                ) : (
                                    <PersonIcon sx={{ fontSize: "16px" }} />
                                ),
                                tooltip: params.row.isActive ? t("users_deactivate") : t("users_activate"),
                                onClick: (e: React.MouseEvent) => {
                                    dispatch(
                                        openUserDeactivateDialog({
                                            id: params.row.id,
                                            isActive: params.row.isActive,
                                            associatedUsers: params.row.associatedUsers ?? [],
                                            username: `${params.row.firstName} ${params.row.lastName}`,
                                            assignedWorkers: params.row.allAssignedWorkersCount,
                                        })
                                    );
                                    e.stopPropagation();
                                },
                            },
                        ]}
                    />
                </Box>
            );
        }
        return null;
    },
}
  ];

  return columns;
};

export default useSupervisorColumns;
